import React, { useState, useCallback } from "react";
import AsyncSelect from "react-select/async";
import debounce from "lodash.debounce";
import { CreateCustomer } from "../components/drawers";
import CollectionModal from "../pages/collection/modal";

interface Option {
  value: string;
  label: string;
}

interface AsyncSelectStyledProps {
  bg?: string;
  border?: boolean;
  accentColor?: string;
  name?: string;
  indicator?: boolean;
  loadOptions: (inputValue: string) => Promise<Option[]>;
  defaultValue?: Option | Option[];
  onChange?: (value: any, actionMeta: any) => void;
  limit?: number;
  isOptionDisabled?: (option: Option) => boolean;
  valueMax?: number;
  isMulti?: boolean;
  isClearable?: boolean;  // Add clearable prop here
  customStyles?: any;
  errorData?: any;
  isDisabled?: boolean;
  className?: string;
  addComponentName?: string;
  debounceDelay?: number;
}

const AsyncSelectStyled: React.FC<AsyncSelectStyledProps> = ({
  loadOptions,
  defaultValue,
  onChange,
  limit,
  valueMax = 3,
  isMulti,
  isClearable = false,  // Default isClearable to false
  customStyles,
  errorData,
  name,
  isDisabled,
  className,
  addComponentName,
  debounceDelay = 300,
  ...rest
}) => {

  const [isValid, setIsValid] = useState(true);
  const [values, setValues] = useState<Option[]>([]);
  const [openDrawer, setOpenDrawer] = useState<string | null>(null);

  const errorMessages = name && errorData && errorData[name] ? errorData[name] : [];

  const onChangeWithLimit = useCallback(
    (value: any, actionMeta: any) => {
      if (onChange) {
        if (
          isMulti &&
          actionMeta.action === "select-option" &&
          valueMax &&
          Array.isArray(value) &&
          value?.length > valueMax
        ) {
          setIsValid(false);
        } else {
          setIsValid(true);
          setValues(value);
          return onChange(value, actionMeta);
        }
      }
    },
    [limit, onChange, isMulti, valueMax]
  );

  const onAddButtonClick = () => {
    console.log('onAddButtonClick');
    if (addComponentName) {
      setOpenDrawer(addComponentName);
    }
  };

  const onDrawerModalClose = () => {
    setOpenDrawer(null);
  };

   const handleOnCreate = (newItem: Option) => {
    setOpenDrawer(null);
    const updatedValues = isMulti ? [...values, newItem] : [newItem];
    setValues(updatedValues);
    if (onChange) {
      onChange(updatedValues, { action: "select-option" });
    }
  };

  // Debounce the API call to avoid flooding requests
  const debouncedLoadOptions = useCallback(debounce(loadOptions, debounceDelay), [loadOptions, debounceDelay]);

  return (
    <div className="flex items-center">
      <div className="flex-grow">
        {(values?.length >= valueMax || !isValid) && (
          <small className="text-danger">
            You can't choose more than {valueMax} items
          </small>
        )}
        <AsyncSelect
          defaultValue={defaultValue}
          loadOptions={debouncedLoadOptions}
          isOptionDisabled={
            values?.length ? () => values?.length >= valueMax : undefined
          }
          instanceId="async-select-id"
          onChange={onChangeWithLimit}
          isMulti={isMulti}
          isClearable={isClearable}
          value={values}
          styles={customStyles}
          isDisabled={isDisabled}
          className={className}
          cacheOptions
          {...rest}
        />
        {errorMessages.length > 0 && (
          <div className="text-red-600 text-sm mt-1">
            {errorMessages.map((error: any, index: number) => (
              <div key={index}>{error}</div>
            ))}
          </div>
        )}
      </div>
      {addComponentName && (
        <button
          type="button"
          onClick={onAddButtonClick}
          className="ml-1 w-7 h-7 flex items-center justify-center text-black bg-white border border-gray-400 rounded-full transition-all duration-300 ease-in-out hover:bg-gray-200 hover:border-gray-300"
        >
          +
        </button>
      )}

      {/* Render Drawer/Modal Based on State */}
      <CreateCustomer isOpen={openDrawer === "customer"} onClose={onDrawerModalClose} onCreate={handleOnCreate} />
      <CollectionModal isOpen={openDrawer === "collections"} onClose={onDrawerModalClose} onSubmit={() => {  }} />

    </div>
  );
};

export default AsyncSelectStyled;